import schema from "generate-schema";
import { Box, Text } from "ink";
import { get } from "lodash";
import React, { FC, useEffect, useMemo, useState } from "react";

import { JobCache, JobInfo, loadConfigFromCommandLine } from "./cache";
import type { ListCliOptions } from "./cli";
import { Config, mergeConfig } from "./config";
import Divider from "./divider";
import { version as clientVersion } from "./version";

interface JobProps {
	job: JobInfo;
	opts: ListCliOptions;
}

const Job: FC<JobProps> = ({ job, opts }) => {
	const cfg = useMemo(() => applyOptions(job, opts), [job, opts]);

	return (
		<>
			<Box paddingY={1}>
				<Divider />
			</Box>

			<Box>
				<Box width={13}>
					<Text>Job:</Text>
				</Box>
				<Text bold>{job.name}</Text>
			</Box>
			<Box>
				<Box width={13}>
					<Text>Type:</Text>
				</Box>
				<Text bold>{job.type}</Text>
			</Box>

			<Box paddingTop={1}>
				<Text>{JSON.stringify(cfg, null, 2)}</Text>
			</Box>
		</>
	);
};

const generateSchema = (name: string, cfg: Config) => {
	return schema.json(name, cfg);
};

interface JobListJsonProps {
	cache: JobCache;
	opts: ListCliOptions;
}

const JobListJson: FC<JobListJsonProps> = ({ cache, opts }) => {
	const data = useMemo(
		() => ({
			clientVersion,
			jobs: cache.jobs.map(j => {
				const s: unknown = opts.verbose
					? generateSchema(j.name, j.defaultConfig)
					: undefined;

				return {
					jobType: j.type,
					canRun: j.canRun,
					errors: j.errors.join("\n"),
					defaultConfig: applyOptions(j, opts),
					schema: s
				};
			})
		}),
		[cache, opts]
	);

	return (
		<Box>
			<Text>{JSON.stringify(data, null, 2)}</Text>
		</Box>
	);
};

interface JobListProps {
	cache: JobCache;
	opts: ListCliOptions;
}

const JobList: FC<JobListProps> = ({ cache, opts }) => {
	if (opts.json) {
		return <JobListJson {...{ cache, opts }} />;
	}

	return (
		<Box flexDirection="column">
			<Box>
				<Text>
					Client Version: <Text bold>v{clientVersion}</Text>
				</Text>
			</Box>

			{cache.jobs.map((job: JobInfo) => (
				<Job key={job.type} job={job} opts={opts} />
			))}
		</Box>
	);
};

function applyOptions(jobInfo: JobInfo, opts: ListCliOptions): Config {
	let cfg = jobInfo.defaultConfig ? jobInfo.defaultConfig : ({} as Config);
	const { name } = jobInfo;

	let jobConfig: Config = { job: name };
	if (opts.verbose) {
		const { name: packageName, version } = loadMainPackageJSON();
		const result = /^@(?<org>.+)\/(?<name>.+)$/g.exec(packageName);

		jobConfig = {
			job: {
				package: get(result, "groups.name", name) as string,
				version,
				type: jobInfo.type
			}
		};
	}

	cfg = mergeConfig(cfg, jobConfig);

	delete cfg.runlyApi;

	if (!opts.verbose) {
		delete cfg.execution;
		delete cfg.results;
	}

	return cfg;
}

interface PackageInfo {
	name: string;
	version: string;
}

// https://stackoverflow.com/a/25394785/316108
function loadMainPackageJSON(attempts = 1): PackageInfo {
	if (attempts > 5) {
		throw new Error("Can't resolve application's package.json file");
	}

	const mainPath = attempts === 1 ? "./" : new Array(attempts).join("../");

	try {
		return require.main.require(`${mainPath}package.json`) as PackageInfo;
	} catch {
		return loadMainPackageJSON(attempts + 1);
	}
}

export interface ParseConfig {
	config: Config;
	error: Error;
}

export const useConfigFile = (
	loadedConfig: Config,
	cache: JobCache
): ParseConfig => {
	const [config, setConfig] = useState<Config>();
	const [error, setError] = useState<Error>();

	useEffect(() => {
		async function doIt() {
			try {
				setConfig(await loadConfigFromCommandLine(loadedConfig, cache));
			} catch (err) {
				setError(err);
			}
		}
		void doIt();
	}, [loadedConfig, cache]);

	return { config, error };
};

export default JobList;
